import { useEffect } from 'react'
import { Route, Routes } from 'react-router'
import { Header } from '@/components/Header'
import { usePerformanceOrgCacheLifecycle } from '@/lib/performanceQueries'
import { useAnalytics, useAuth, useSurface } from '@/provider/useAuth'
import History from '@/routes/History'
import Landing from '@/routes/Landing'
import NotFound from '@/routes/NotFound'
import Performance from '@/routes/Performance'
import RunDashboard from '@/routes/RunDashboard'
import Schedules from '@/routes/Schedules'
import SiteHealth from '@/routes/SiteHealth'
import SiteHealthPageInspector from '@/routes/SiteHealthPageInspector'
import SiteHealthReport from '@/routes/SiteHealthReport'
import type { TrackerAppProps } from './TrackerApp.types'

export function TrackerApp(_props: TrackerAppProps) {
  const auth = useAuth()
  const analytics = useAnalytics()
  const surface = useSurface()

  usePerformanceOrgCacheLifecycle(auth?.orgId ?? undefined)

  // One application-view event per mount. `surface` (free vs embedded) is
  // stamped automatically by the provider; `authed` distinguishes a logged-in
  // viewer from an anonymous one within either shell.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire-once-on-mount
  useEffect(() => {
    analytics.track('app_viewed', { authed: auth !== null })
  }, [])

  return (
    <div className="tracker-surface relative flex min-h-full flex-col bg-background text-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 [background-image:linear-gradient(to_right,hsl(var(--foreground)/0.035)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--foreground)/0.035)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:linear-gradient(to_bottom,black,transparent_70%)]"
      />
      {surface !== 'embedded' && <Header />}
      <div className="relative z-10 flex flex-1 flex-col">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/run/:runId" element={<RunDashboard />} />
          {auth && <Route path="/history" element={<History />} />}
          {auth && <Route path="/schedules" element={<Schedules />} />}
          {auth && <Route path="/performance" element={<Performance />} />}
          {auth && (
            <>
              <Route path="/site-health" element={<SiteHealth />} />
              <Route
                path="/site-health/:auditId"
                element={<SiteHealthReport />}
              />
              <Route
                path="/site-health/:auditId/pages/:pageId"
                element={<SiteHealthPageInspector />}
              />
            </>
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  )
}
